import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"
import { type LucideIcon } from "lucide-react"
import { forwardRef, type AnchorHTMLAttributes, type ReactNode } from "react"

import Spinner from "@/components/shared/Spinner"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip"
import { cn } from "@/lib/utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-all duration-200 focus-visible:outline-hidden focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default:
          "bg-primary text-primary-foreground shadow-sm hover:bg-primary/90",
        destructive:
          "bg-destructive text-destructive-foreground shadow-xs hover:bg-destructive/90",
        outline:
          "border border-input shadow-xs hover:bg-accent hover:text-accent-foreground",
        secondary:
          "bg-secondary text-secondary-foreground shadow-xs hover:bg-secondary/80",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        link: "text-primary underline-offset-4 hover:underline",
        crimson:
          "bg-crimson-500 text-white hover:bg-crimson-600",
        bare: "hover:text-gray-600 transition-colors",
      },
      size: {
        "icon-xs": "h-3 rounded-sm text-xs [&_svg]:size-3",
        "icon-sm": "h-3.5 rounded-sm text-xs [&_svg]:size-3.5",
        "icon-md": "h-4 rounded-sm text-xs [&_svg]:size-4",
        xs: "h-6 rounded-sm px-2 text-xs [&_svg]:size-4",
        sm: "h-8 rounded-md px-3 text-xs [&_svg]:size-4",
        md: "h-9 px-4 py-2 [&_svg]:size-4",
        lg: "h-10 rounded-md px-8 [&_svg]:size-5",
        // Legacy aliases for compatibility with the original button.tsx sizes.
        default: "h-9 px-4 py-2 [&_svg]:size-4",
        icon: "h-9 w-9 [&_svg]:size-4",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "md",
    },
  }
)

type TooltipSide = "top" | "right" | "bottom" | "left"

type AnchorLinkProps = Pick<
  AnchorHTMLAttributes<HTMLAnchorElement>,
  "href" | "target" | "rel" | "download" | "hrefLang" | "referrerPolicy"
>

export interface ButtonProps
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, keyof AnchorLinkProps>,
    AnchorLinkProps,
    VariantProps<typeof buttonVariants> {
      text?: string
      icon?: LucideIcon
      loadingText?: string
      loading?: boolean
      children?: ReactNode
      tooltip?: ReactNode
      tooltipSide?: TooltipSide
      tooltipClassName?: string
      /** Applied to the text span — e.g. to hide labels inside a compact toolbar. */
      textClassName?: string
      asChild?: boolean
}

const spinnerSizeMap = {
  "icon-xs": "xs",
  "icon-sm": "xs",
  "icon-md": "xs",
  xs: "xs",
  sm: "sm",
  md: "sm",
  lg: "md",
  default: "sm",
  icon: "sm",
} as const satisfies Record<NonNullable<ButtonProps["size"]>, "xs" | "sm" | "md">

const Button = forwardRef<HTMLButtonElement | HTMLAnchorElement, ButtonProps>(function Button(
  {
    className,
    variant,
    size,
    loading = false,
    loadingText,
    text,
    icon: Icon,
    children,
    tooltip,
    tooltipSide = "top",
    tooltipClassName,
    textClassName,
    href,
    target,
    rel,
    download,
    hrefLang,
    referrerPolicy,
    asChild = false,
    ...props
  },
  ref,
) {
  const displayText = loading ? loadingText : text
  const spinnerSize = spinnerSizeMap[size ?? "md"]
  const iconOnly = Icon && !text && !loadingText && !children

  const content = (text || Icon) ? (
    <>
      {loading ? <Spinner size={spinnerSize} /> : Icon && <Icon />}
      {displayText != null && displayText !== '' && (
        <span className={textClassName}>{displayText}</span>
      )}
    </>
  ) : (
    <>
      {loading && <Spinner size={spinnerSize} />}
      {children}
    </>
  )

  const sharedClassName = cn(
    buttonVariants({ variant, size, className }),
    iconOnly && "px-0 aspect-square shrink-0",
  )

  let renderedEl: React.ReactElement
  if (asChild) {
    // Slot merges our className/props onto the single child element. Pass
    // `children` directly (no text/icon/spinner composition) since Slot
    // requires exactly one child.
    renderedEl = (
      <Slot ref={ref} className={sharedClassName} {...props}>
        {children}
      </Slot>
    )
  } else if (href !== undefined) {
    // Default rel for new-tab links to prevent reverse-tabnabbing.
    const safeRel = rel ?? (target === "_blank" ? "noopener noreferrer" : undefined)
    const { disabled, type: _ignoredType, ...anchorProps } = props as React.ButtonHTMLAttributes<HTMLButtonElement>
    renderedEl = (
      <a
        ref={ref as React.Ref<HTMLAnchorElement>}
        href={href}
        target={target}
        rel={safeRel}
        download={download}
        hrefLang={hrefLang}
        referrerPolicy={referrerPolicy}
        aria-disabled={disabled || loading || undefined}
        className={sharedClassName}
        {...(anchorProps as AnchorHTMLAttributes<HTMLAnchorElement>)}
      >
        {content}
      </a>
    )
  } else {
    renderedEl = (
      <button
        ref={ref as React.Ref<HTMLButtonElement>}
        className={sharedClassName}
        disabled={props.disabled || loading}
        {...props}
      >
        {content}
      </button>
    )
  }

  if (!tooltip) return renderedEl

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>{renderedEl}</TooltipTrigger>
        <TooltipContent
          side={tooltipSide}
          className={cn("max-w-[240px] text-xs font-normal", tooltipClassName)}
        >
          {tooltip}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
})
Button.displayName = "Button"

export { Button, buttonVariants }
